/**
 * AiSuggestionsInbox — Liste der pending KI-Vorschläge.
 *
 * Wählt pro Suggestion-`kind` einen spezialisierten Renderer
 * (z.B. RefeedingSuggestionCard) und fällt für unbekannte kinds
 * auf einen generischen Fallback-Renderer zurück. Damit kann eine
 * neue Capability ohne UI-Change zumindest ihre rationale anzeigen.
 */

import React from 'react';
import { Inbox } from 'lucide-react';
import { useAiCompanion } from '@/contexts/AiCompanionContext';
import { RefeedingSuggestionCard } from './RefeedingSuggestionCard';
import { WeightTrendSuggestionCard } from './WeightTrendSuggestionCard';
import { SessionBriefSuggestionCard } from './SessionBriefSuggestionCard';
import { MalnutritionRiskSuggestionCard } from './MalnutritionRiskSuggestionCard';
import { DropOutSuggestionCard } from './DropOutSuggestionCard';
import { PreVisitIntakeSuggestionCard } from './PreVisitIntakeSuggestionCard';
import { GncpDraftSuggestionCard } from './GncpDraftSuggestionCard';
import { StrategyHealthSuggestionCard } from './StrategyHealthSuggestionCard';
import { GenericSuggestionCard } from './GenericSuggestionCard';
import type { AiSuggestion } from '@/lib/ai/types';

export const AiSuggestionsInbox: React.FC = () => {
  const { suggestions, isLoading } = useAiCompanion();

  return (
    <div className="flex max-h-[60vh] flex-col">
      <div className="border-b px-4 py-3">
        <h3 className="text-sm font-semibold">KI-Vorschläge</h3>
        <p className="text-xs text-muted-foreground">
          Hintergrund-Watcher, die zur Prüfung vorgemerkt sind.
        </p>
      </div>

      <div className="overflow-y-auto">
        {isLoading && (
          <div className="px-4 py-6 text-sm text-muted-foreground">
            Lade …
          </div>
        )}

        {!isLoading && suggestions.length === 0 && (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center text-sm text-muted-foreground">
            <Inbox className="h-6 w-6 opacity-50" />
            <span>Keine offenen Vorschläge.</span>
          </div>
        )}

        {!isLoading &&
          suggestions.map((s: AiSuggestion) => (
            <SuggestionRow key={s.id} suggestion={s} />
          ))}
      </div>
    </div>
  );
};

const SuggestionRow: React.FC<{ suggestion: AiSuggestion }> = ({
  suggestion,
}) => {
  switch (suggestion.kind) {
    case 'refeeding_risk':
      return <RefeedingSuggestionCard suggestion={suggestion} />;
    case 'weight_trend':
      return <WeightTrendSuggestionCard suggestion={suggestion} />;
    case 'session_brief':
      return <SessionBriefSuggestionCard suggestion={suggestion} />;
    case 'malnutrition_risk':
      return <MalnutritionRiskSuggestionCard suggestion={suggestion} />;
    case 'drop_out_risk':
      return <DropOutSuggestionCard suggestion={suggestion} />;
    case 'pre_visit_intake_dispatched':
      return <PreVisitIntakeSuggestionCard suggestion={suggestion} />;
    case 'gncp_draft':
      return <GncpDraftSuggestionCard suggestion={suggestion} />;
    case 'strategy_health':
      return <StrategyHealthSuggestionCard suggestion={suggestion} />;
    default:
      return <GenericSuggestionCard suggestion={suggestion} />;
  }
};
